import { useEffect, useState } from 'react'
import {
  Box, Container, Typography, TextField, Button, Stack, Alert,
  Autocomplete, Chip,
} from '@mui/material'
import { IconMail, IconArrowRight } from '@tabler/icons-react'
import { tokens } from '@/theme'
import { trackFunnel } from '@/utils/analytics'
import { apiUrl } from '@/lib/apiBase'

// Posts to the local FastAPI (/subscribe) through the dev proxy. The proxy maps
// /api/agro/* -> the API root (stripping the prefix), so /api/agro/subscribe
// reaches POST /subscribe. See src/web/vite.config.ts.
const SUBSCRIBE_URL = '/api/agro/subscribe'
const GSA_SUMMARY_URL = '/api/agro/gsas/summary'

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

type Status = 'idle' | 'submitting' | 'success' | 'error'

interface GsaOption {
  gsa_id: number
  name: string
  county: string | null
}

interface NewsletterSignupProps {
  /**
   * Pre-scope the signup to a single GSA (T-203 pilot page). When set, this
   * GSA is pre-selected as an interest so subscribers coming from the tenant
   * page get the GSA interest row without having to pick it — and it is still
   * submitted even if the GSA-summary options fail to load.
   */
  gsaId?: number
  /** Display label for the pre-scoped GSA chip (falls back to a blank label). */
  gsaName?: string
}

export default function NewsletterSignup({ gsaId, gsaName }: NewsletterSignupProps = {}) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState('')

  // GSA interest state. When the component is pre-scoped to a GSA (gsaId), that
  // GSA seeds the selection so its interest is always included in the payload.
  const [gsaOptions, setGsaOptions] = useState<GsaOption[]>([])
  const [selectedGsas, setSelectedGsas] = useState<GsaOption[]>(
    gsaId != null ? [{ gsa_id: gsaId, name: gsaName ?? '', county: null }] : [],
  )
  const [gsaLoadError, setGsaLoadError] = useState(false)

  // Fetch GSA summary options once on mount
  useEffect(() => {
    fetch(apiUrl(GSA_SUMMARY_URL))
      .then((res) => {
        if (!res.ok) throw new Error(`GSA fetch failed (${res.status})`)
        return res.json() as Promise<GsaOption[]>
      })
      .then((data) => setGsaOptions(data))
      .catch(() => setGsaLoadError(true))
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    const trimmed = email.trim()
    if (!EMAIL_RE.test(trimmed)) {
      setStatus('error')
      setError('Please enter a valid email address.')
      return
    }

    // Privacy-safe funnel: record the attempt and how many GSA interests were
    // chosen (a coarse count only — never the GSA ids or the email). (T-147)
    trackFunnel('signup', {
      surface: 'newsletter_section',
      step: 'submit',
      count: selectedGsas.length,
    })

    setStatus('submitting')
    try {
      const res = await fetch(apiUrl(SUBSCRIBE_URL), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmed,
          name: name.trim() || undefined,
          gsa_ids: selectedGsas.map((g) => g.gsa_id),
        }),
      })

      // Never trust a bare 2xx: a static host (e.g. CloudFront with no API
      // route behind it) can answer with an HTTP-200 that is actually the
      // SPA's index.html, and treating that as success silently loses the
      // lead. Require res.ok AND a JSON content-type AND a JSON-object body.
      if (!res.ok) throw new Error(`Request failed (${res.status})`)
      const contentType = res.headers?.get?.('content-type') ?? ''
      if (!contentType.toLowerCase().includes('application/json')) {
        throw new Error('Unexpected non-JSON response')
      }
      const body: unknown = await res.json()
      if (typeof body !== 'object' || body === null || Array.isArray(body)) {
        throw new Error('Unexpected response shape')
      }

      trackFunnel('signup', { surface: 'newsletter_section', step: 'success' })
      setStatus('success')
      setEmail('')
      setName('')
      setSelectedGsas([])
    } catch {
      // Keep the entered values so the visitor can simply retry.
      trackFunnel('signup', { surface: 'newsletter_section', step: 'error' })
      setStatus('error')
      setError('Something went wrong — email not saved. Please retry.')
    }
  }

  return (
    <Box sx={{ background: tokens.green['50'], py: { xs: 8, md: 10 } }}>
      <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
        <Box
          sx={{
            display: 'inline-flex',
            p: 1.25,
            borderRadius: 1.5,
            background: alphaGreen(),
            color: tokens.green.main,
            mb: 2,
          }}
        >
          <IconMail size={28} stroke={1.5} />
        </Box>
        <Typography variant="overline" sx={{ color: tokens.green.main, display: 'block', mb: 1 }}>
          News
        </Typography>
        <Typography variant="h2" sx={{ fontSize: { xs: '1.9rem', md: '2.4rem' }, mb: 1.5 }}>
          Stay in the Loop
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
          Monthly updates on groundwater, GSA activity, and irrigation data for
          California's agricultural communities. No spam — unsubscribe anytime.
        </Typography>

        {status === 'success' ? (
          <Alert severity="success" sx={{ textAlign: 'left' }}>
            Thanks for subscribing! Check your inbox for a confirmation.
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                label="Name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                disabled={status === 'submitting'}
                slotProps={{ htmlInput: { maxLength: 255 } }}
              />
              <TextField
                label="Email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                disabled={status === 'submitting'}
                error={status === 'error'}
                slotProps={{ htmlInput: { maxLength: 255 } }}
              />
              {/* GSA interest multi-select */}
              {!gsaLoadError && gsaOptions.length > 0 && (
                <Autocomplete
                  multiple
                  options={gsaOptions}
                  getOptionLabel={(opt) =>
                    opt.county ? `${opt.name} (${opt.county})` : opt.name
                  }
                  isOptionEqualToValue={(opt, val) => opt.gsa_id === val.gsa_id}
                  value={selectedGsas}
                  onChange={(_, newValue) => setSelectedGsas(newValue)}
                  disabled={status === 'submitting'}
                  renderValue={(value, getItemProps) =>
                    value.map((option, index) => {
                      const { key, ...itemProps } = getItemProps({ index })
                      return (
                        <Chip
                          key={key}
                          label={option.name}
                          size="small"
                          {...itemProps}
                        />
                      )
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="GSA interests (optional)"
                      placeholder="Select GSAs you care about"
                      helperText="Receive targeted updates for the GSAs you select."
                    />
                  )}
                />
              )}
              {status === 'error' && error && (
                <Alert severity="error" sx={{ textAlign: 'left' }}>{error}</Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={status === 'submitting'}
                endIcon={<IconArrowRight size={18} />}
              >
                {status === 'submitting' ? 'Subscribing…' : 'Subscribe'}
              </Button>
            </Stack>
          </Box>
        )}
      </Container>
    </Box>
  )
}

// Small helper kept local to avoid importing alpha into a one-off usage.
function alphaGreen() {
  return 'rgba(26, 51, 37, 0.08)'
}
